import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchNewBranches2024 } from "../api/getApi";
import ShareModal from "../components/shareModal";

const NewBranches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [uniqueRegions, setUniqueRegions] = useState([]);
  const [uniqueTypes, setUniqueTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareTextVisible, setIsShareTextVisible] = useState(false);

  const currentPageUrl = window.location.href;

  // Effect to show and hide share text
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setIsShareTextVisible(true);
    }, 300);
    const timer = setTimeout(() => {
      setIsShareTextVisible(false);
    }, 2500);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer1);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchNewBranches2024();
        
        setBranches(response.data);
        
        const regions = [...new Set(response.data.map(branch => branch.region))].sort();
        const types = [...new Set(response.data.map(branch => branch.type))].sort();
        
        setUniqueRegions(regions);
        setUniqueTypes(types);
      } catch (err) {
        setError("Error fetching data: " + err.message);
        console.error("Error fetching new branches:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBranches();
  }, []);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredBranches = useMemo(() => {
    let filteredData = branches.filter(branch => {
      if (regionFilter !== "all" && branch.region !== regionFilter) return false;
      if (typeFilter !== "all" && branch.type !== typeFilter) return false;
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        return (
          branch.inst_name.toLowerCase().includes(searchTermLower) ||
          branch.branch_code.toLowerCase().includes(searchTermLower) ||
          branch.place.toLowerCase().includes(searchTermLower) ||
          branch.inst_code.toLowerCase().includes(searchTermLower) ||
          branch.dist.toLowerCase().includes(searchTermLower)
        );
      }
      return true;
    });

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (typeof a[sortConfig.key] === 'number') {
          return sortConfig.direction === 'ascending' 
            ? a[sortConfig.key] - b[sortConfig.key]
            : b[sortConfig.key] - a[sortConfig.key];
        }
        
        const valA = String(a[sortConfig.key] || '').toLowerCase();
        const valB = String(b[sortConfig.key] || '').toLowerCase();

        if (valA < valB) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [branches, regionFilter, typeFilter, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBranches.slice(indexOfFirstItem, indexOfLastItem);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    
    return (
      <span className="ml-1">
        {sortConfig.direction === 'ascending' ? (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
        <p className="mt-4 text-lg text-gray-700">Loading new branch data for 2024...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-lg text-center">
          <p className="font-bold mb-2">Error Loading Data</p>
          <p>{error}</p>
          <button 
            onClick={() => {
              setLoading(true);
              setError(null);
              window.location.reload();
            }}
            className="mt-4 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <Helmet>
        <title>New Branches 2024 | CollegeRank</title>
        <meta name="description" content="Explore new engineering and pharmacy branches added in 2024 across colleges in Andhra Pradesh." />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-4">
          <Link
            to="/"
            className="text-blue-600 hover:underline font-medium flex items-center gap-1"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </Link>
        </div>
        
        <div className="bg-white shadow-md rounded-lg">
          <div className="p-6 border-b flex justify-between items-start sm:items-center relative">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">New Branches Added in 2024</h1>
              <p className="text-gray-600 mt-1">
                Explore {branches.length > 0 ? branches.length : '...'} newly added branches across colleges in Andhra Pradesh for 2024 academic year.
              </p>
            </div>
            <div
              className="relative flex flex-col items-end"
              onMouseEnter={() => setIsShareTextVisible(true)}
              onMouseLeave={() => setIsShareTextVisible(false)}
            >
              <div
                className={`
                  absolute top-[-2.5rem] right-0 bg-blue-300 text-gray-800 px-4 py-2 rounded-lg shadow-lg 
                  text-sm font-medium transition-all duration-300 ease-in-out whitespace-nowrap z-50
                  ${isShareTextVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"}
                `}
              >
                Share With Friends
              </div>
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="ml-4 mt-2 sm:mt-0 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg flex items-center shadow-sm transition-colors text-sm"
                aria-label="Share this page"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.001l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
                </svg>
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>

          <div className="p-6 border-b bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by college, branch, location..."
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-4 py-2 border"
                />
              </div>
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <select
                  id="region"
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-4 py-2 border"
                >
                  <option value="all">All Regions</option>
                  {uniqueRegions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Type
                </label>
                <select
                  id="type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-4 py-2 border"
                >
                  <option value="all">All Types</option>
                  {uniqueTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="px-6 py-3 bg-gray-100 border-b">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{filteredBranches.length}</span> results
                {regionFilter !== "all" && (
                  <span> in <span className="font-medium">{regionFilter}</span> region</span>
                )}
                {typeFilter !== "all" && (
                  <span> of type <span className="font-medium">{typeFilter}</span></span>
                )}
              </p>
              <div className="flex items-center mt-2 sm:mt-0">
                <label htmlFor="page-size" className="mr-2 text-sm text-gray-700">
                  Show:
                </label>
                <select
                  id="page-size"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="rounded border-gray-300 text-sm py-1 px-2 border"
                >
                  {[15, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {filteredBranches.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('inst_name')}
                    >
                      <div className="flex items-center">
                        College {getSortDirectionIndicator('inst_name')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('branch_code')}
                    >
                      <div className="flex items-center">
                        Branch {getSortDirectionIndicator('branch_code')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('place')}
                    >
                      <div className="flex items-center">
                        Location {getSortDirectionIndicator('place')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('type')}
                    >
                      <div className="flex items-center">
                        Type {getSortDirectionIndicator('type')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('affilia_univ')}
                    >
                      <div className="flex items-center">
                        University {getSortDirectionIndicator('affilia_univ')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('college_fee')}
                    >
                      <div className="flex items-center">
                        Fee (₹) {getSortDirectionIndicator('college_fee')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('convener_seats')}
                    >
                      <div className="flex items-center">
                        Seats {getSortDirectionIndicator('convener_seats')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((branch) => (
                    <tr key={branch._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="font-medium text-blue-600">{branch.inst_name}</div>
                        <div className="text-xs text-gray-500">{branch.inst_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {branch.branch_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>{branch.place}</div>
                        <div className="text-xs text-gray-500">
                          {branch.dist} • {branch.region}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {branch.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {branch.affilia_univ}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        ₹{branch.college_fee.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {branch.convener_seats}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No branches found matching your criteria.</p>
                <button 
                  onClick={() => {
                    setSearchTerm("");
                    setRegionFilter("all");
                    setTypeFilter("all");
                    setSortConfig({ key: null, direction: 'ascending' });
                  }}
                  className="mt-4 text-blue-600 hover:underline"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>

          {filteredBranches.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t flex flex-col sm:flex-row items-center justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${
                    currentPage > 1
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  First
                </button>
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${
                    currentPage > 1
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${
                    currentPage < totalPages
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${
                    currentPage < totalPages
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Understanding New Branches</h2>
            <p className="text-gray-600">
              These branches have been newly introduced for the 2024 academic year. Students should verify seat availability
              and eligibility criteria before making decisions. Fee structures and specific branch details may be subject to changes.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Important Notice</h2>
            <p className="text-gray-600">
              The information displayed is based on the latest data available from official sources. For the most up-to-date
              information, please consult the official websites of respective universities or contact the institutions directly.
            </p>
          </div>
        </div>
      </div>
      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={currentPageUrl}
      />
    </div>
  );
};

export default NewBranches;